import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AdminDeposit, useGetDepositsQuery } from '../../api/deposit/depositAdminApiSlice';
import Permission from '../../common/permissions';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import RowActionsMenu from '../../components/RowActionsMenu';
import SegmentedTabs from '../../components/SegmentedTabs';
import TableCard, { TableHeaderRow, tableCellClass } from '../../components/TableCard';
import { getCurrentLang } from '../../i18n/i18n';
import { makeHasPermissionSelector } from '../../selector/authenticationSelector';
import { formatCurrency, formatDateTime } from '../../utils';
import CaptureDepositModal from './components/CaptureDepositModal';
import ReleaseDepositModal from './components/ReleaseDepositModal';

const PAGE_SIZE = 50;

type DepositTabKey = 'all' | 'open' | 'action_needed' | 'captured' | 'released';

// Tab → status sets sent as the comma-separated `status` query param;
// undefined = no filter (spec §4). Values are the backend enum, lowercase.
const TAB_STATUSES: Record<DepositTabKey, string[] | undefined> = {
    all: undefined,
    open: ['pending', 'authorized'],
    action_needed: ['capture_failed', 'failed', 'expired'],
    captured: ['captured_full', 'captured_partial'],
    released: ['released'],
};

const KNOWN_STATUSES = [
    'pending',
    'authorized',
    'capture_failed',
    'captured_full',
    'captured_partial',
    'released',
    'expired',
    'failed',
];

// Statuses in which the hold is still cancelable/capturable — release and
// capture actions show only here (backend 409s otherwise).
const ACTIONABLE_STATUSES = ['authorized', 'capture_failed'];

// Deposits ops view — orders with a deposit hold, built against the §4
// contract of docs/plans/deposit-stripe-hold-runtime-spec.md. The list is
// visible to any tenant admin; release/capture (real-money actions) are
// additionally gated on the `manage:deposits` permission.
const DepositsPage = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState<DepositTabKey>('open');
    const [page, setPage] = useState(0);
    const { data, isLoading, isError } = useGetDepositsQuery({
        statuses: TAB_STATUSES[tab],
        page,
        pageSize: PAGE_SIZE,
    });
    const hasManageDeposits = useSelector(
        makeHasPermissionSelector(Permission.manageDeposits()),
    );
    const [releaseTarget, setReleaseTarget] = useState<AdminDeposit | null>(null);
    const [captureTarget, setCaptureTarget] = useState<AdminDeposit | null>(null);

    // One locale for every date + currency call on the page. getCurrentLang
    // already falls back to 'de', the narrowing only satisfies formatDateTime.
    const lang = getCurrentLang() === 'en' ? 'en' : 'de';
    const items = data?.items ?? [];
    const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

    // Reaction: the result set can shrink after a release/capture refetch
    // (tag invalidation) — clamp an out-of-range page instead of stranding
    // the user on an empty page without pagination controls.
    useEffect(() => {
        if (data && page > totalPages - 1) setPage(totalPages - 1);
    }, [data, page, totalPages]);

    // Compare backend status values case-insensitively, per the API-values rule.
    const statusLabel = (status: string) => {
        const normalized = status.toLowerCase();
        return KNOWN_STATUSES.includes(normalized)
            ? t(`deposits.status.${normalized}`)
            : status;
    };

    const isActionable = (deposit: AdminDeposit) =>
        ACTIONABLE_STATUSES.includes(deposit.depositStatus.toLowerCase());

    return (
        <div className={'px-4 sm:px-6 py-6 max-w-7xl'}>
            <PageHeader>
                <PageHeadline>{t('deposits.title')}</PageHeadline>
            </PageHeader>

            <SegmentedTabs<DepositTabKey>
                className={'mb-4'}
                items={[
                    { key: 'all', label: t('deposits.tabs.all') },
                    { key: 'open', label: t('deposits.tabs.open') },
                    { key: 'action_needed', label: t('deposits.tabs.action_needed') },
                    { key: 'captured', label: t('deposits.tabs.captured') },
                    { key: 'released', label: t('deposits.tabs.released') },
                ]}
                value={tab}
                onChange={(key) => {
                    setTab(key);
                    // Server-side pagination: a new filter means a new result
                    // set, so an out-of-range page must not stick around.
                    setPage(0);
                }}
            />

            {isLoading ? (
                <div className={'flex justify-center py-16'}>
                    <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
                </div>
            ) : isError ? (
                <p className={'text-sm text-foreground/60'}>
                    {t('deposits.load_error')}
                </p>
            ) : (
                <>
                    <TableCard>
                        <TableHeaderRow
                            labels={[
                                t('deposits.columns.order'),
                                t('deposits.columns.product'),
                                t('deposits.columns.customer'),
                                t('deposits.columns.amount'),
                                t('deposits.columns.status'),
                                t('deposits.columns.expires'),
                                '',
                            ]}
                        />
                        <tbody>
                            {items.map((deposit) => (
                                <tr
                                    key={deposit.orderId}
                                    className={'border-b border-foreground/5 last:border-b-0'}
                                >
                                    <td className={tableCellClass}>{deposit.orderNumber}</td>
                                    <td className={tableCellClass}>{deposit.groupName}</td>
                                    <td className={tableCellClass}>
                                        {/* Null for anonymized orders. */}
                                        {deposit.customerName ?? (
                                            <span className={'italic text-foreground/40'}>
                                                {t('common.not_applicable')}
                                            </span>
                                        )}
                                    </td>
                                    <td className={tableCellClass}>
                                        {formatCurrency(deposit.depositAmount, lang)}
                                        {deposit.capturedAmount != null && (
                                            <div className={'text-xs text-foreground/50'}>
                                                {t('deposits.captured_of', {
                                                    captured: formatCurrency(
                                                        deposit.capturedAmount,
                                                        lang,
                                                    ),
                                                    total: formatCurrency(
                                                        deposit.depositAmount,
                                                        lang,
                                                    ),
                                                })}
                                            </div>
                                        )}
                                    </td>
                                    <td className={tableCellClass}>
                                        <span
                                            className={
                                                'bg-foreground/5 rounded-full px-2 py-0.5 whitespace-nowrap'
                                            }
                                        >
                                            {statusLabel(deposit.depositStatus)}
                                        </span>
                                    </td>
                                    <td className={tableCellClass}>
                                        {deposit.authExpiresAt ? (
                                            formatDateTime(deposit.authExpiresAt, lang, {
                                                includeTime: true,
                                            })
                                        ) : (
                                            <span className={'italic text-foreground/40'}>
                                                {t('common.not_applicable')}
                                            </span>
                                        )}
                                    </td>
                                    <td className={`${tableCellClass} text-right whitespace-nowrap`}>
                                        {/* "Freigeben" is the one frequent changing action
                                            and stays visible; "Einbehalten" sits in the row's
                                            menu (PO, 2026-11-03; design-decisions.md ›
                                            Zeilenaktionen in Tabellen, rule 5). */}
                                        {hasManageDeposits && isActionable(deposit) && (
                                            <span className={'inline-flex items-center gap-2'}>
                                                <button
                                                    type={'button'}
                                                    className={'text-primary hover:underline'}
                                                    onClick={() => setReleaseTarget(deposit)}
                                                >
                                                    {t('deposits.actions.release')}
                                                </button>
                                                <RowActionsMenu
                                                    rowName={deposit.orderNumber}
                                                    items={[{
                                                        id: 'capture',
                                                        label: t('deposits.actions.capture'),
                                                        onClick: () => setCaptureTarget(deposit),
                                                    }]}
                                                />
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={7} className={`${tableCellClass} text-foreground/40 italic`}>
                                        {t('deposits.empty')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </TableCard>

                    {totalPages > 1 && (
                        <div className={'flex items-center justify-end gap-3 mt-3 text-sm'}>
                            <button
                                type={'button'}
                                className={
                                    'text-primary hover:underline disabled:text-foreground/30 disabled:no-underline'
                                }
                                disabled={page === 0}
                                onClick={() => setPage(page - 1)}
                            >
                                {t('deposits.pagination.previous')}
                            </button>
                            <span className={'text-foreground/60'}>
                                {t('deposits.pagination.page_of', {
                                    page: page + 1,
                                    total: totalPages,
                                })}
                            </span>
                            <button
                                type={'button'}
                                className={
                                    'text-primary hover:underline disabled:text-foreground/30 disabled:no-underline'
                                }
                                disabled={page >= totalPages - 1}
                                onClick={() => setPage(page + 1)}
                            >
                                {t('deposits.pagination.next')}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Mounted per row so the modal state initializes from the row. */}
            {releaseTarget && (
                <ReleaseDepositModal
                    deposit={releaseTarget}
                    onClose={() => setReleaseTarget(null)}
                />
            )}
            {captureTarget && (
                <CaptureDepositModal
                    deposit={captureTarget}
                    onClose={() => setCaptureTarget(null)}
                />
            )}
        </div>
    );
};

export default DepositsPage;
